// An app's write approval: what will change, row by row, and the person's
// decision. The person can leave any row out; Approve says how many rows it
// covers. The decision goes to PUB with the version the person saw, so a
// change in between comes back as a conflict showing the current record, never
// as a silent overwrite. An outcome that cannot be confirmed offers a status
// check only, never a repeat of the write.
import { useMemo, useState } from 'react';
import { Callout } from '@/components/ui/content';
import { StatusPill } from '@/components/ui/kit';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appApprovalsCopy from '@/i18n/ns/app-approvals';
import { useDecisionsApi, type AppDecisionsApi } from './app-decisions-api';
import type { AppApproval, AppDecision } from './app-decision-types';
import {
  BlockedLine,
  NoticeLine,
  OriginLine,
  UnknownBlock,
  decisionView,
  isDecidable,
  statusLabel,
  statusTone,
} from './approval-parts';
import { RowDiff } from './row-diff';
import { useAppDecision } from './use-app-decision';
import './app-cards.css';

export function AppApprovalCard({
  id,
  initial = null,
  api: explicitApi,
  onSettled,
}: {
  id: string;
  initial?: AppApproval | null;
  api?: AppDecisionsApi;
  onSettled?: (record: AppDecision) => void;
}) {
  const copy = useNamespaceCopy(appApprovalsCopy);
  const api = useDecisionsApi(explicitApi);
  const { record, loadFailed, busy, lost, stillOpen, notice, decide, refresh, checkStatus } =
    useAppDecision(api, 'approval', id, initial, onSettled);
  const [left, setLeft] = useState<ReadonlySet<string>>(new Set());
  const approval = record?.kind === 'approval' ? record : null;
  // A new version of the record starts with nothing left out.
  const version = approval?.version;
  const [seen, setSeen] = useState(version);
  if (version !== seen) {
    setSeen(version);
    setLeft(new Set());
  }
  const excluded = useMemo(
    () => (approval?.state === 'pending' ? left : new Set(approval?.excludedRowIds ?? [])),
    [approval?.state, approval?.excludedRowIds, left],
  );
  if (!approval)
    return (
      <Callout tone={loadFailed ? 'danger' : 'info'}>
        {loadFailed ? copy.loadFailed : copy.loading}
        {loadFailed && (
          <>
            {' '}
            <button type="button" className="ws-text-button" onClick={() => void refresh()}>
              {copy.checkStatus}
            </button>
          </>
        )}
      </Callout>
    );
  const view = decisionView(approval, lost);
  const total = approval.rows.length;
  const included = approval.rows.filter((row) => !excluded.has(row.rowId));
  const decidable = isDecidable(approval, lost);
  const toggle = (rowId: string) =>
    setLeft((current) => {
      const next = new Set(current);
      if (!next.delete(rowId)) next.add(rowId);
      return next;
    });
  const approveLabel =
    included.length === total
      ? formatCopy(copy.approveAll, { total })
      : formatCopy(copy.approveSome, { included: included.length, total });
  return (
    <section className="ws-app-card" data-card="app-approval" data-view={view}>
      <header className="ws-app-card-head">
        <div>
          <h3>{formatCopy(copy.writeTitle, { target: approval.targetLabel })}</h3>
          <p>{formatCopy(copy.writeSummaryAll, { count: total, app: approval.appTitle })}</p>
          <OriginLine copy={copy} origin={approval.origin} forName={approval.requesterName} />
        </div>
        <StatusPill tone={statusTone(view)}>{statusLabel(copy, view)}</StatusPill>
      </header>
      <BlockedLine copy={copy} block={approval} />
      {view === 'unknown' ? (
        <UnknownBlock
          copy={copy}
          busy={busy}
          still={stillOpen}
          onCheck={() => void checkStatus()}
        />
      ) : null}
      <NoticeLine copy={copy} notice={notice} />
      {total === 0 ? (
        <p className="ws-app-card-note">{copy.noRows}</p>
      ) : (
        <RowDiff
          copy={copy}
          rows={approval.rows}
          excluded={excluded}
          disabled={busy}
          selectable={decidable}
          onToggle={toggle}
        />
      )}
      {(view === 'approved' || view === 'applied') && approval.appliedRowCount !== null && (
        <p className="ws-app-card-note">
          {formatCopy(copy.appliedSome, { count: approval.appliedRowCount })}
        </p>
      )}
      {view === 'rejected' && <p className="ws-app-card-note">{copy.rejectedBody}</p>}
      {view === 'pending' && !approval.canDecide && (
        <p className="ws-app-card-note">{copy.notAllowed}</p>
      )}
      {decidable && included.length === 0 && total > 0 && (
        <p className="ws-app-card-note">{copy.allLeftOut}</p>
      )}
      {decidable && (
        <div className="ws-app-card-actions">
          <button
            type="button"
            className="ws-button primary"
            disabled={busy || included.length === 0}
            onClick={() => void decide({ decision: 'approve', excludedRowIds: [...left] })}
          >
            {busy ? copy.working : approveLabel}
          </button>
          <button
            type="button"
            className="ws-button quiet"
            disabled={busy}
            onClick={() => void decide({ decision: 'reject' })}
          >
            {copy.reject}
          </button>
        </div>
      )}
    </section>
  );
}
